import Dialog from '../1. Core/Controls/Dialog/Dialog';
import TextField from '../1. Core/Controls/TextField/TextField';
import NumField from '../1. Core/Controls/TextField/NumField';
import ListBox from '../1. Core/Controls/ListBox/ListBox';
import Select from '../1. Core/Controls/Select/Select';
import Button from '../1. Core/Controls/Button/Button';
import {ServerAPI} from '../1. Core/ServerAPI';
import NP from '../1. Core/NP';
import SimpleAthletesList from '../2. Athletes/SimpleAthletesList';
import {IAthleteListItem} from '../2. Athletes/AthletesList';
import {IAthlete, PageMode, SportType} from '../10. Battle/types';
import {getWeights, Weight} from '../10. Battle/Util';
import {athleteToWeightClassKey} from './fighterCardV2WeightClassKey';

type AthleteSource = 'existing' | 'new';

export default class CreateFighterCardV2SpecialDialog extends Dialog {
	private _standardWeightKeys: string[] = [];
	private _mmaWeights: Weight[] = [];
	private _weightClassSelect: Select;
	private _fullName: TextField;
	private _athleteSource: ListBox;
	private _athleteName: TextField;
	private _pickAthleteBtn: Button;
	private _selectedAthlete: IAthlete | null;
	private _firstName: TextField;
	private _lastName: TextField;
	private _photoUrl: TextField;
	private _force: NumField;
	private _speed: NumField;
	private _takedown: NumField;
	private _control: NumField;
	private _defense: NumField;
	private _efficiency: NumField;

	constructor(props?: { EvOkButtonClick?: () => void }) {
		super(props || {});
	}

	_initFields(settings: { EvOkButtonClick?: () => void }) {
		super._initFields(settings);
		this._Caption = 'Fighter card v2 — special';
		this._CloseOnFrameMouseDown = true;
		this._ShowHeader = true;
		this._ShowCloseButton = true;
		this._OkRK = 'Save';
		this._AutoDestroy = true;
		this._Mode = 'FullHeight';
		this._Width = 440;
		this._Height = 560;
		this._selectedAthlete = null;
	}

	_render() {
		super._render();

		this._athleteSource = new ListBox({
			ParentNode: this._contentNode,
			Items: [
				{Content: 'Existing athlete', Value: 'existing', IsRadioButton: true},
				{Content: 'Create new athlete', Value: 'new', IsRadioButton: true},
			],
			EvItemSelected: () => this.updateAthleteFieldsVisible(),
		});
		this._athleteSource.setValue('existing', false);

		this._athleteName = new TextField({
			ParentNode: this._contentNode,
			FloatingLabel: 'Athlete',
			EnableEdit: false,
		});

		this._pickAthleteBtn = new Button({
			ParentNode: this._contentNode,
			Content: 'Search athlete…',
			EvClick: () => this.openAthletePicker(),
		});

		this._firstName = new TextField({
			ParentNode: this._contentNode,
			FloatingLabel: 'First name',
		});
		this._lastName = new TextField({
			ParentNode: this._contentNode,
			FloatingLabel: 'Last name',
		});
		this._photoUrl = new TextField({
			ParentNode: this._contentNode,
			FloatingLabel: 'Photo url',
		});

		this._fullName = new TextField({
			ParentNode: this._contentNode,
			FloatingLabel: 'Card full name',
		});

		this._weightClassSelect = new Select({
			ParentNode: this._contentNode,
			FloatingLabel: 'Weight class',
		});

		this._force = new NumField({
			ParentNode: this._contentNode,
			FloatingLabel: 'Force',
			Value: 50,
			Min: 0,
			Max: 100,
			FractionCount: 0,
		});
		this._speed = new NumField({
			ParentNode: this._contentNode,
			FloatingLabel: 'Speed',
			Value: 50,
			Min: 0,
			Max: 100,
			FractionCount: 0,
		});
		this._takedown = new NumField({
			ParentNode: this._contentNode,
			FloatingLabel: 'Takedown',
			Value: 50,
			Min: 0,
			Max: 100,
			FractionCount: 0,
		});
		this._control = new NumField({
			ParentNode: this._contentNode,
			FloatingLabel: 'Control',
			Value: 50,
			Min: 0,
			Max: 100,
			FractionCount: 0,
		});
		this._defense = new NumField({
			ParentNode: this._contentNode,
			FloatingLabel: 'Defense',
			Value: 50,
			Min: 0,
			Max: 100,
			FractionCount: 0,
		});
		this._efficiency = new NumField({
			ParentNode: this._contentNode,
			FloatingLabel: 'Efficiency',
			Value: 50,
			Min: 0,
			Max: 100,
			FractionCount: 0,
		});

		void this.loadWeightClasses();
		this.updateAthleteFieldsVisible();
	}

	private async loadWeightClasses() {
		const [keys, weights] = await Promise.all([
			ServerAPI.get<string[]>('/fighter-cards/v2/weight-classes'),
			getWeights({Type: SportType.MMA}) as Promise<Weight[]>,
		]);
		this._standardWeightKeys = keys || [];
		this._mmaWeights = weights || [];
		this._weightClassSelect.setItems(this._standardWeightKeys.map(k => ({Content: k, Value: k})));
		if (this._standardWeightKeys.length) {
			this._weightClassSelect.setValue(this._standardWeightKeys[0], false);
		}
	}

	private updateAthleteFieldsVisible() {
		const isNew = this._athleteSource.getValue() === 'new';
		this._athleteName.setIsVisible(!isNew);
		this._pickAthleteBtn.setIsVisible(!isNew);
		this._firstName.setIsVisible(isNew);
		this._lastName.setIsVisible(isNew);
		this._photoUrl.setIsVisible(isNew);
		this._force.setIsVisible(isNew);
		this._speed.setIsVisible(isNew);
		this._takedown.setIsVisible(isNew);
		this._control.setIsVisible(isNew);
		this._defense.setIsVisible(isNew);
		this._efficiency.setIsVisible(isNew);
		this._weightClassSelect.setEnabled(isNew);
		if (isNew) {
			this._selectedAthlete = null;
			this._athleteName.setValue('');
			this._fullName.setValue('');
		}
	}

	private athleteFullName(athlete: IAthlete): string {
		if (athlete.LastName) {
			return `${athlete.LastName} ${athlete.FirstName}`.trim();
		}
		return (athlete.FirstName || '').trim();
	}

	private onAthleteSelected(item: IAthleteListItem) {
		const athlete = item.Athlete;
		this._selectedAthlete = athlete;
		this._athleteName.setValue(`${item.Name} (${athlete.Id})`);
		this._fullName.setValue(this.athleteFullName(athlete));
		this.applyAthleteWeight(athlete);
	}

	private applyAthleteWeight(athlete: IAthlete) {
		const weight = this._mmaWeights.find(w => w.Id === athlete.WeightCategory);
		const key = athleteToWeightClassKey(athlete, weight, this._standardWeightKeys);
		if (key) {
			this._weightClassSelect.setValue(key, false);
			this._weightClassSelect.setEnabled(false);
			return;
		}
		this._weightClassSelect.setEnabled(true);
		NP.showMessage({
			Content: 'Could not map athlete weight to v2 weight class — select manually',
		});
	}

	private openAthletePicker() {
		let picker: Dialog;
		picker = new Dialog({
			AutoDestroy: true,
			Caption: 'Select athlete',
			ShowHeader: true,
			ShowCloseButton: true,
			ShowSearch: true,
			ShowButtons: false,
			Width: 640,
			Height: 520,
			Class: 'nopad',
			Content: new SimpleAthletesList({
				Mode: PageMode.mmaathletes,
				Width: 640,
				Height: 456,
				EvAthleteSelected: (sender: object, item: IAthleteListItem) => {
					this.onAthleteSelected(item);
					picker.hide();
				},
			}),
			EvSearch: (sender: object, e: { Value: string }) => {
				(picker.getContent() as SimpleAthletesList).setFilter(e.Value);
			},
		});
		picker.show();
		(picker.getContent() as SimpleAthletesList).refresh();
	}

	async _onOkButtonClick() {
		const weightClassKey = (this._weightClassSelect.getValue() || '').trim();
		const fullName = (this._fullName.getValue() || '').trim();
		if (!weightClassKey || !fullName) {
			NP.showMessage({Content: 'Weight class and full name required'});
			return;
		}

		const source = this._athleteSource.getValue() as AthleteSource;
		const body: {
			weightClassKey: string;
			fullName: string;
			athleteId?: number;
			newAthlete?: {
				firstName: string;
				lastName: string;
				photoUrl: string;
				force: number;
				speed: number;
				takedown: number;
				control: number;
				defense: number;
				efficiency: number;
			};
		} = {weightClassKey, fullName};

		if (source === 'existing') {
			if (!this._selectedAthlete?.Id) {
				NP.showMessage({Content: 'Select athlete'});
				return;
			}
			body.athleteId = this._selectedAthlete.Id;
		} else {
			const firstName = (this._firstName.getValue() || '').trim();
			const lastName = (this._lastName.getValue() || '').trim();
			if (!firstName && !lastName) {
				NP.showMessage({Content: 'Athlete name required'});
				return;
			}
			body.newAthlete = {
				firstName,
				lastName,
				photoUrl: (this._photoUrl.getValue() || '').trim(),
				force: this._force.getValue() || 0,
				speed: this._speed.getValue() || 0,
				takedown: this._takedown.getValue() || 0,
				control: this._control.getValue() || 0,
				defense: this._defense.getValue() || 0,
				efficiency: this._efficiency.getValue() || 0,
			};
		}

		const catalogId = await ServerAPI.post<number>('/fighter-cards/v2/catalog/special', body);
		NP.showMessage({Content: `Special card created, catalog id ${catalogId}`});
		this.EvOkButtonClick.fire(this);
		this.hide();
	}
}
